import {isFeatureEnabled} from 'feature-flags';
import {Parameters} from 'platform/components';
import {Align, Display, HStack, VStack} from 'platform/foundation';
import {useFormatNumber} from 'platform/locale';
import {match, Pattern} from 'ts-pattern';

import {isNotNil} from 'ramda-adjunct';

import {buildArray, Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {VehicleLogo} from '../../../components/VehicleLogo/VehicleLogo';
import {useCatalogue} from '../../../hooks/useCatalogue';
import {useVehicleTranslatedInfo} from '../../../hooks/useVehicleTranslatedInfo';
import i18n from '../../../i18n/i18n';
import {featureFlags} from '../../../utils/featureFlags';
import {useCreatePageContext} from '../components/CreatePageContext';
import {VEHICLETYPE_PASSENGER_CAR} from '../constants/vehiclePassengerCar';

export function VehicleHeader(props: TestIdProps) {
  const {values: vehicle, has} = useCreatePageContext();
  const catalogue = useCatalogue();

  const formatNumber = useFormatNumber();

  const {data} = useVehicleTranslatedInfo({
    make: vehicle.make,
    modelFamily: vehicle.modelFamily,
    vehicleType: VEHICLETYPE_PASSENGER_CAR,
    trimLevel: vehicle.trimLevel,
  });
  const vehicleName = match([data.make, data.model])
    .with([Pattern.nonNullable, Pattern.nullish], () => data.make)
    .with([Pattern.nonNullable, Pattern.nonNullable], () => `${data.make} ${data.model}`)
    .otherwise(() => undefined);

  const showFuelType = has.basicInfo;
  const showTransmission = showFuelType && has.fuelType;
  const showDrive = showTransmission && has.transmission;
  const showPower = showDrive && has.drive;
  const showBodyStyle = showPower && has.power;
  const showMileage = showBodyStyle && has.bodyStyle;

  const parameters = buildArray<string | Nullish>()
    .when(isNotNil(vehicle.firstRegistrationOnYear), vehicle.firstRegistrationOnYear?.toString())
    .when(
      isNotNil(vehicle.mileage) && showMileage,
      `${formatNumber(vehicle.mileage)} ${i18n.t('general.metric.km')}`
    )
    .when(
      isFeatureEnabled(featureFlags.PR_SPLIT_POWER) &&
        isNotNil(vehicle.maxPower ?? vehicle.power) &&
        showPower,
      `${vehicle.maxPower ?? vehicle.power} ${i18n.t('general.metric.kW')}`
    )
    .when(
      !isFeatureEnabled(featureFlags.PR_SPLIT_POWER) && isNotNil(vehicle.power) && showPower,
      `${vehicle.power} ${i18n.t('general.metric.kW')}`
    )
    .when(showTransmission, catalogue.getTransmission(vehicle.transmission?.[0]))
    .when(showFuelType, catalogue.getFuelType(vehicle.fuelType?.[0]))
    .when(showDrive, catalogue.getDrive(vehicle.drive?.[0]))
    .when(showBodyStyle, catalogue.getCarStyle(vehicle.bodyStyle?.[0]));

  return (
    <VStack align="center" spacing={4}>
      <HStack wrap spacing={4} align="center" justify="center">
        <VehicleLogo
          vehicle={{
            make: vehicle.make,
            modelFamily: vehicle.modelFamily,
            vehicleType: VEHICLETYPE_PASSENGER_CAR,
          }}
          height={14}
          width={14}
          color="white"
          data-testid={suffixTestId('vehicleLogo', props)}
        />
        <Align center>
          <Display size={1} color="white" data-testid={suffixTestId('vehicleHeading', props)}>
            {vehicleName}
          </Display>
        </Align>
      </HStack>
      <Align center>
        <Parameters
          parameters={parameters}
          color="white"
          size="base"
          justify="center"
          data-testid={suffixTestId('vahicleParameters', props)}
        />
      </Align>
    </VStack>
  );
}
